import { useRequireOrganization } from '@startupquickstart/frontend';
import { HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NewTicketDialog } from './NewTicketDialog';

/**
 * Globally-visible Support shortcut. Sits in the top-right of the layout
 * and opens the new-ticket modal pre-set to a "bug" report with the
 * current page URL filled in — so the common "something on this page is
 * wrong" path is one click away from anywhere in the app.
 *
 * On the support page itself the URL would be misleading ("you're filing
 * a bug about /internal/support") so we suppress the prefill there. The
 * user can also clear the field manually inside the form.
 */
export function SupportButton() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { hasOrganization, isInternal } = useRequireOrganization();

  // Internal-only feature; renders nothing for personal accounts and
  // non-internal organizations so the icon doesn't tease access they
  // can't use.
  if (!hasOrganization || !isInternal) return null;

  const isOnSupportPage = location.pathname.startsWith('/internal/support');
  const initialUrl = isOnSupportPage
    ? ''
    : typeof window !== 'undefined'
      ? window.location.href
      : '';

  return (
    <>
      {/*
        Renders inline inside the shared <Layout>'s `headerActions` slot.
        The slot wraps us in vertical-center + horizontal padding and adds
        separators on either side. Sized to match the Clerk UserButton's
        avatar (32×32) so the top bar's right cluster has consistent
        circular hit-targets.
       */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open support"
        title="Report a bug or request a feature"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <HelpCircle className="h-4 w-4" />
      </button>
      <NewTicketDialog
        open={open}
        onOpenChange={setOpen}
        defaultType="bug"
        initialUrl={initialUrl}
      />
    </>
  );
}
